import type { ApplicationStatus, ApplicationWithStats } from '@owl/shared';
import { useCallback, useMemo, useState } from 'react';
import { api } from '../../../lib/api';
import { computeAppHealth } from '../../../lib/appHealth';
import { timeAgo } from '../../../lib/time';
import { useLoader } from '../../../lib/useLoader';
import { Icon } from '../../icons';
import type { MobileShellCtx } from '../MobileShellCtx';
import { formatCompact } from '../charts';

type StatusFilter = 'all' | ApplicationStatus;

const STATUS_FILTERS: { key: StatusFilter; label: string }[] = [
  { key: 'all', label: 'Todas' },
  { key: 'active', label: 'Activas' },
  { key: 'paused', label: 'Pausadas' },
  { key: 'revoked', label: 'Revocadas' },
];

export function AppsPage({ ctx }: { ctx: MobileShellCtx }) {
  const [apps, setApps] = useState<ApplicationWithStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<StatusFilter>('all');

  const load = useCallback(
    async (isCancelled: () => boolean) => {
      setLoading(true);
      setErr(null);
      try {
        const r = await api.listApplications(ctx.environment);
        if (!isCancelled()) setApps(r.items);
      } catch (e) {
        if (!isCancelled()) setErr((e as Error).message);
      } finally {
        if (!isCancelled()) setLoading(false);
      }
    },
    [ctx.environment],
  );

  useLoader(load, ctx.refreshTick);

  const searched = useMemo(() => {
    const q = search.toLowerCase();
    return apps.filter(
      (a) =>
        !q ||
        a.name.toLowerCase().includes(q) ||
        a.company.toLowerCase().includes(q) ||
        a.product.toLowerCase().includes(q) ||
        a.instance.toLowerCase().includes(q),
    );
  }, [apps, search]);

  const counts = useMemo(() => {
    const c: Record<StatusFilter, number> = {
      all: searched.length,
      active: 0,
      paused: 0,
      revoked: 0,
    };
    for (const a of searched) c[a.status] += 1;
    return c;
  }, [searched]);

  const groups = useMemo(() => {
    const filtered = status === 'all' ? searched : searched.filter((a) => a.status === status);
    const grouped = new Map<string, ApplicationWithStats[]>();
    for (const a of filtered) {
      const arr = grouped.get(a.product) ?? [];
      arr.push(a);
      grouped.set(a.product, arr);
    }
    return [...grouped.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [searched, status]);

  return (
    <div className="m-page">
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <div
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 9,
            padding: '9px 12px',
            background: 'var(--bg-2)',
            border: '1px solid var(--border-1)',
            borderRadius: 10,
          }}
        >
          <Icon.Search size={14} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar app o cliente…"
            style={{
              background: 'transparent',
              color: 'var(--fg-1)',
              border: 'none',
              outline: 'none',
              fontSize: 13.5,
              flex: 1,
              fontFamily: 'inherit',
            }}
          />
          {search && (
            <button type="button" onClick={() => setSearch('')} aria-label="Clear">
              <Icon.X size={14} />
            </button>
          )}
        </div>
        <button
          type="button"
          className="m-iconbtn"
          onClick={() => ctx.openSheet({ kind: 'search' })}
          aria-label="Search globally"
          title="Búsqueda global"
        >
          <Icon.Search size={16} />
        </button>
      </div>

      <div className="m-tabs">
        {STATUS_FILTERS.map((f) => (
          <button
            key={f.key}
            type="button"
            className={`m-tab${status === f.key ? ' active' : ''}`}
            onClick={() => setStatus(f.key)}
          >
            {f.label}
            <span className="count">{counts[f.key]}</span>
          </button>
        ))}
      </div>

      {loading && <div className="m-loading">Cargando…</div>}
      {err && <div className="m-error-banner">{err}</div>}

      {groups.map(([product, items]) => (
        <div key={product}>
          <div className="m-section-h">
            <span className="m-section-t">
              {product}
              <span
                className="mono"
                style={{ marginLeft: 8, color: 'var(--m-fg-4)', letterSpacing: 0 }}
              >
                · {items.length}
              </span>
            </span>
          </div>
          <section className="m-card">
            {items.map((a) => {
              const ratio = 1 - (a.errorRate24h ?? 0);
              const pct = Math.round(ratio * 100);
              const cls = pct >= 95 ? '' : pct >= 85 ? 'warn' : 'err';
              const health = computeAppHealth(a);
              return (
                <button
                  key={a.id}
                  type="button"
                  className="app-row"
                  onClick={() =>
                    ctx.navigate({ name: 'appdash', applicationId: a.id, tab: 'logs' })
                  }
                >
                  <div className="app-avatar">{a.product.slice(0, 2)}</div>
                  <div className="info">
                    <div className="name">
                      <span className={`dot health-${health}`} />
                      {a.name}
                    </div>
                    <div className="meta">
                      <span>
                        {a.company}
                        {a.instance ? ` · ${a.instance}` : ''}
                      </span>
                      <span className="dot-sep">·</span>
                      <span>{formatCompact(a.logs24h)} logs</span>
                      <span className="dot-sep">·</span>
                      <span>{a.lastLogAt ? timeAgo(a.lastLogAt) : 'sin logs'}</span>
                    </div>
                  </div>
                  <div className="right">
                    <span className={`pct ${cls}`}>{pct}%</span>
                    <div className={`hbar ${cls}`}>
                      <span style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                </button>
              );
            })}
          </section>
        </div>
      ))}

      {!loading && groups.length === 0 && <div className="m-empty">Ninguna app coincide</div>}
      <div style={{ height: 8 }} />
    </div>
  );
}
